import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Icons,
  TextField,
  Separator,
  useTheme,
  Text,
  useMediaQuery,
  Badge,
  Typography,
} from '@ublend-npm/aulaui-core';
import { withRouter } from 'react-router';
import {
  selectorsResponsive,
  operations,
  operationsResponsive,
} from '@app/store/conversations';
import { mutedConversations } from '@core/notifications/selectors';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector, useDispatch } from 'react-redux';
import changeConversationOpen from '@core/conversations/operationsResponsive/changeConversationOpen.action';
import { DARK } from '@core/constants/theme';
import { useFlags } from 'launchdarkly-react-client-sdk';
import pushRoute from '@app/utils/routing';
import { viewportHeightWithOffset } from '@app/utils/viewportHeight';
import { currentUserId } from '../../../../core/selectors/user';
import {
  conversationLatestMessage,
  filteredConversationQuery,
} from '../../../../core/conversations/selectorsResponsive';
import ConversationListCard from './ConversationListCard';
import CreateConversationModal from './CreateConversationModal';
import InfiniteTable from '../common/InfiniteTable';
import NavigationBackHeader from '../common/NavigationBackHeader/NavigationBackHeader';
import { allowedBackButtonRoutes } from '@core/constants/backButtonNavigation';
import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  MESSAGE_ARCHIVE_VIEWED,
  MESSAGE_CONVERSATION_OPENED,
  MESSAGE_NEW_INITIATED,
} from '@core/constants/analytics';

const ConverstationsMenu = ({ location, router }: any) => {
  const { webAppFtResponsiveMessages202506 } = useFlags();
  const { palette, breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const spaceId = useSelector(getCurrentSpaceId);
  const mutedConversation = useSelector(mutedConversations);
  const dispatch = useDispatch();
  // Redux selectors
  const searchQuery = useSelector(filteredConversationQuery) || '';
  const paginatedConversations = useSelector(
    selectorsResponsive.paginatedConversations
  );
  const filteredConversations = useSelector(
    selectorsResponsive.visibleConversations
  );
  const conversations = searchQuery
    ? filteredConversations
    : paginatedConversations;
  const fetchingConversations = useSelector(
    (state: any) => state.inboxv2.fetchingConversations
  );
  const loadingMoreConversations = useSelector(
    selectorsResponsive.loadingMoreConversations
  );
  const totalCount = useSelector((state: any) =>
    selectorsResponsive.totalCount(state)
  );
  const fetchError = useSelector((state: any) => state.inboxv2.error);
  const userId = useSelector(currentUserId);
  // Calculate total unread conversations count
  const totalUnreadCount = useSelector((state: any) => {
    if (!userId) return 0;
    return conversations.filter((conversation: any) => {
      const isUnread =
        selectorsResponsive.isConversationUnreadBy &&
        selectorsResponsive.isConversationUnreadBy(
          state,
          conversation.id,
          userId
        );
      if (!isUnread) return false;
      // Only count as unread if the latest message was NOT sent by current user
      const latestMessage = conversationLatestMessage(state, conversation.id);
      return latestMessage?.writer !== userId;
    }).length;
  });

  // Changing document title
  useEffect(() => {
    document.title = `Messages - Aula`;
  }, []);

  // Conversations are fetched at app start
  // If the above fails, we use the lack of conversations as sign to try again
  useEffect(() => {
    if (!conversations.length) {
      dispatch(operations?.fetchConversations());
    }
  }, [dispatch]);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    dispatch(operationsResponsive.filterConversations(query));
  };
  // Get current conversation ID to highlight selected conversation
  const currentConversationId = window.location.hash
    .split('/')
    .pop()
    ?.split('?')[0];

  const handleOpenConversation = () => {
    // Trigger analytics event when user click new messages
    analyticsTrackEvent(MESSAGE_NEW_INITIATED);
    dispatch(changeConversationOpen(true));
  };

  const NoUserFound = () => {
    return (
      <Box
        tabIndex={0}
        bgcolor={palette.background.default}
        p="1rem 0.5rem 1.3rem 0.5rem"
        width="100%"
        borderRadius="0.5rem"
        position="absolute"
        top={0}
        textAlign="center"
      >
        <Typography fontWeight="400" color="inherit" fontSize="1rem">
          No messages found.
        </Typography>
        <Text
          kind="p3"
          color={palette.mode === DARK ? '#FFFFFFB2' : '#00000099'}
          fontSize="0.875rem"
        >
          Check spelling and try again.
        </Text>
      </Box>
    );
  };

  if (!webAppFtResponsiveMessages202506) {
    pushRoute('/dashboard');
  }

  // Navgation Header style
  const navigationBackHeaderStyle = {
    width: '100%',
    marginBottom: '16px',
    marginLeft: '-0.5rem',
  };

  // Fetch query params from hash
  const getQueryParams = () => {
    const hash = window.location.hash;
    const queryParams = hash?.includes('?') ? hash.split('?')[1] : '';
    return new URLSearchParams(queryParams);
  };

  const queryParams = getQueryParams();
  // Path from where this page is redirected
  const fromRoutes = (queryParams.get('from') || '')
    .split(',')
    .map((params) => params.trim())
    .filter(Boolean);
  // Only show back button if 'from' parameter exist and is in allowed routes list
  const showBackButton =
    queryParams?.has('from') &&
    fromRoutes?.every((route) => allowedBackButtonRoutes?.includes(route));

  // Construct the back navigation URL based on the source
  const getBackNavigationRoute = () => {
    const hasEducatorArea = fromRoutes.includes('educatorArea');
    const hasMySpaceNav = fromRoutes.includes('myspacenav');

    if (hasEducatorArea) {
      // Base route
      let route = `/dashboard/${spaceId}/journey/educatorArea`;
      // Append myspacenav if present
      if (hasMySpaceNav) {
        route += `?from=myspacenav`;
      }
      return route;
    }

    if (fromRoutes.includes('search')) {
      return `/search`;
    }
    // Default (participants, groups, etc.)
    return `/dashboard/${spaceId}/people/${
      hasMySpaceNav ? `?from=myspacenav` : ''
    }`;
  };

  const handleConversationClick = (
    conversationId: string,
    isConversationArchived: boolean | undefined
  ) => {
    // Trigger analytics event when user click on any of the existing conversation
    analyticsTrackEvent(MESSAGE_CONVERSATION_OPENED);
    // Trigger analytics event when user click on archived conversation
    if (isConversationArchived) {
      analyticsTrackEvent(MESSAGE_ARCHIVE_VIEWED);
    }
    // Navigate to conversation
    window.location.href = `/#/dashboard/${spaceId}/messages/${conversationId}${
      showBackButton
        ? `?from=${encodeURIComponent(queryParams.get('from') || '')}`
        : ''
    }`;
  };

  const desktopHeight = viewportHeightWithOffset(65);
  const mobileHeight = viewportHeightWithOffset(62);

  // We'll use the original sorted conversations array to maintain proper ordering
  // This ensures that conversations with new messages appear at the top regardless of type
  return (
    webAppFtResponsiveMessages202506 && (
      <Box
        width={{ xs: '100%', sm: '100%', md: '360px' }}
        mt="8px"
        height={isDesktop ? desktopHeight : mobileHeight}
        marginTop={isDesktop ? '4px' : '0'}
        display="flex"
        bgcolor={palette.background.default}
      >
        <Box
          width="100%"
          sx={{ padding: '16px' }}
          height="100%"
          bgcolor={palette.background.paper}
        >
          <Box>
            {showBackButton && (
              <NavigationBackHeader
                wrapperStyle={navigationBackHeaderStyle}
                route={getBackNavigationRoute()}
              />
            )}
            <Box
              sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
              bgcolor={palette.background.paper}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: '8px',
                }}
              >
                <Text
                  aria-label={`Messages${
                    totalUnreadCount > 0
                      ? `, ${totalUnreadCount} unread message${
                          totalUnreadCount > 1 ? 's' : ''
                        }`
                      : ''
                  }`}
                  tabIndex={0}
                  sx={{ fontSize: '1.25rem' }}
                >
                  Messages
                </Text>

                {totalUnreadCount > 0 && (
                  <Badge
                    aria-label={`${totalUnreadCount} unread message${
                      totalUnreadCount > 1 ? 's' : ''
                    }`}
                    role="status"
                    aria-live="polite"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'grid',
                      placeContent: 'center',
                      minWidth: '20px',
                      height: '20px',
                      padding: '0 6px',
                      borderRadius: '10px',
                      backgroundColor:
                        palette.mode === DARK ? '#000000' : '#EAE8ED',
                    }}
                  >
                    {Number(totalUnreadCount) > 99 ? '99+' : totalUnreadCount}
                  </Badge>
                )}
              </Box>
              <Button
                kind="primary"
                size="small"
                aria-label="Compose new conversation"
                sx={{
                  display: 'flex',
                  fontSize: '0.875rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  textTransform: 'none',
                  width: '136px',
                  '&:hover': {
                    backgroundColor: palette.primary.dark,
                  },
                }}
                onClick={handleOpenConversation}
              >
                {' '}
                <Icons.EditOutlined sx={{ fontSize: '20px' }} /> New message
              </Button>
              <CreateConversationModal
                queryParams={
                  showBackButton && fromRoutes?.length > 0
                    ? {
                        from: encodeURIComponent(queryParams.get('from') || ''),
                      }
                    : {}
                }
              />
            </Box>
            <TextField
              label=""
              placeholder="Search"
              leftElement={<Icons.Search />}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <Separator />
          </Box>
          <Box
            mt="16px"
            sx={{
              height: '84%',
              overflowY: 'auto',
            }}
          >
            {fetchError ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                flexDirection="column"
                gap="16px"
              >
                <Text color="error.main" textAlign="center">
                  Failed to load conversations
                </Text>
                <Button
                  size="small"
                  kind="primary"
                  // onClick={handleRefreshConversations}
                >
                  Try Again
                </Button>
              </Box>
            ) : fetchingConversations ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Text>Loading conversations...</Text>
              </Box>
            ) : conversations.length > 0 ? (
              <InfiniteTable
                items={conversations}
                keyExtractor={(conversation) => conversation.id}
                renderItem={(conversation: any) => (
                  <ConversationListCard
                    conversation={conversation}
                    conversationMuted={!!mutedConversation?.[conversation.id]}
                    selected={currentConversationId === conversation.id}
                    onClick={() =>
                      handleConversationClick(
                        conversation.id,
                        conversation?.closed
                      )
                    }
                  />
                )}
                hasNext={
                  !searchQuery && totalCount > paginatedConversations.length
                }
                loadNext={() => {
                  if (!searchQuery) {
                    const nextPage =
                      Math.floor(paginatedConversations.length / 20) + 1;
                    if (operationsResponsive?.loadMoreConversations) {
                      dispatch(
                        operationsResponsive.loadMoreConversations(
                          false,
                          nextPage
                        )
                      );
                    }
                  }
                }}
                loading={loadingMoreConversations}
                renderEmptyList={() => null}
                renderHeader={() => null}
                renderFooter={() => null}
                viewStyle={{ width: '100%', marginBottom: '16px' }}
              />
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                flexDirection="column"
                gap="16px"
                position="relative"
              >
                {searchQuery ? (
                  <NoUserFound />
                ) : (
                  <>
                    <Box
                      tabIndex={0}
                      bgcolor={palette.background.default}
                      p="1rem 0.5rem 1.3rem 0.5rem"
                      width="100%"
                      borderRadius="0.5rem"
                      position="absolute"
                      top={0}
                      textAlign="center"
                    >
                      <Typography
                        fontWeight="400"
                        color="inherit"
                        fontSize="1rem"
                      >
                        No conversations found.
                      </Typography>
                      <Text
                        kind="p3"
                        color={
                          palette.mode === DARK ? '#FFFFFFB2' : '#00000099'
                        }
                        fontSize="0.875rem"
                      >
                        Start a conversation to connect with others.
                      </Text>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  );
};
export default withRouter(ConverstationsMenu);
